import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Phone, Menu, X, ArrowUpRight } from "lucide-react";
import { SITE, NAV } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 left-0 right-0 z-40 bg-[#F6F3EB]/90 backdrop-blur-md border-b border-[#D8D4CA]"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0" onClick={() => setOpen(false)}>
              <img
                src="/logo.png"
                alt="BM MECHANICS — Atelier mécanique Prilly"
                className="h-10 w-10 md:h-12 md:w-12 object-contain"
              />
              <span className="hidden xs:block sm:block">
                <span className="block font-display font-black uppercase tracking-tight text-lg xl:text-xl leading-none text-[#121212]">
                  BM Mechanics
                </span>
                <span className="block font-mono text-[9px] md:text-[10px] uppercase tracking-[0.24em] text-[#8C7B5D]">
                  Prilly · Vaud
                </span>
              </span>
            </Link>

            <nav className="hidden xl:flex items-center gap-7" data-testid="header-nav">
              {NAV.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) => `bm-navlink ${isActive ? "active" : ""}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2 xl:gap-3 shrink-0">
              <a
                href={SITE.phoneHref}
                data-testid="header-call-btn"
                className="hidden xl:flex items-center gap-2 font-mono text-xs tracking-[0.08em] whitespace-nowrap text-[#121212] hover:text-[#8C7B5D] transition-colors px-3 py-2"
              >
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {SITE.phoneDisplay}
              </a>
              <Link
                to="/devis"
                data-testid="header-quote-btn"
                className="hidden xl:inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-[11px] uppercase tracking-[0.16em] whitespace-nowrap px-5 py-3 hover:bg-[#8C7B5D] transition-colors"
              >
                Demander un devis
                <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="header-mobile-call-btn"
                aria-label="Appeler BM Mechanics"
                className="xl:hidden inline-flex items-center justify-center h-10 w-10 border border-[#D8D4CA] text-[#121212]"
              >
                <Phone className="h-4 w-4" />
              </a>
              <button
                data-testid="header-menu-btn"
                aria-label="Ouvrir le menu"
                onClick={() => setOpen(true)}
                className="xl:hidden inline-flex items-center justify-center h-10 w-10 bg-[#121212] text-[#F6F3EB]"
              >
                <Menu className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 bg-[#121212] text-[#F6F3EB] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="flex items-center justify-between h-16 px-4 border-b border-[#33353B]">
              <span className="font-display font-black uppercase text-lg tracking-tight">BM Mechanics</span>
              <button
                data-testid="mobile-menu-close-btn"
                aria-label="Fermer le menu"
                onClick={() => setOpen(false)}
                className="inline-flex items-center justify-center h-10 w-10 border border-[#33353B]"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-6 gap-1">
              {[...NAV, { to: "/devis", label: "Demander un devis", testid: "mobile-menu-devis" }].map((item, i) => (
                <motion.div
                  key={item.to + item.label}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-menu-link-${item.testid}`}
                    onClick={() => setOpen(false)}
                    className={`group flex items-center justify-between py-4 border-b border-[#242529] font-display text-3xl font-bold uppercase tracking-tight ${
                      location.pathname === item.to ? "text-[#8C7B5D]" : "text-[#F6F3EB]"
                    }`}
                  >
                    {item.label}
                    <ArrowUpRight className="h-6 w-6 text-[#4A4D53] group-hover:text-[#8C7B5D] transition-colors" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="px-6 pb-24 pt-4">
              <a href={SITE.phoneHref} data-testid="mobile-menu-call-btn" className="font-mono text-sm tracking-[0.1em] text-[#9A968C]">
                {SITE.phoneDisplay}
              </a>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A4D53] mt-2">{SITE.location}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
